import Navigation from "@/components/Navigation"
import Footer from "@/components/Footer"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  ArrowLeft,
  FolderPlus,
  Folder,
  MoreVertical,
  Pencil,
  Trash2,
  Upload,
  Heart,
  ImageIcon,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from "react"
import { useParams, Link } from "react-router-dom"
import { cn } from "@/lib/utils"

interface AlbumFolder {
  id: number
  name: string
}

interface Picture {
  id: number
  object_type?: string
  index?: number
  image_url: string
  src: string
  width?: number
  height?: number
  is_like?: number
}

function csrfToken() {
  return typeof document !== "undefined"
    ? document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
    : ""
}

function postForm(url: string, params: Record<string, string>) {
  const body = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => body.append(k, v))
  return fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body: body.toString(),
    credentials: "same-origin",
  })
}

// Données de démonstration (aperçu sans backend).
const demoFolders: AlbumFolder[] = [
  { id: 1, name: "Tous les souvenirs" },
  { id: 2, name: "Plage" },
  { id: 3, name: "Soirées" },
]

const demoPictures = (albumId: number): Picture[] =>
  Array.from({ length: 8 }, (_, i) => {
    const seed = albumId * 100 + i
    return {
      id: seed,
      object_type: "picture",
      index: i,
      image_url: `https://picsum.photos/seed/${seed}/1200/900`,
      src: `https://picsum.photos/seed/${seed}/600/600`,
      width: 1200,
      height: 900,
      is_like: i % 3 === 0 ? 1 : 0,
    }
  })

export default function AlbumPhotos() {
  const { id: projectId } = useParams<{ id: string }>()
  const sid = projectId ?? ""

  const [folders, setFolders] = useState<AlbumFolder[]>([])
  const [loadingFolders, setLoadingFolders] = useState(true)
  const [activeId, setActiveId] = useState<number | null>(null)
  const [pictures, setPictures] = useState<Picture[]>([])
  const [loadingPictures, setLoadingPictures] = useState(false)
  const [demo, setDemo] = useState(false)
  const [projectName, setProjectName] = useState<string>("")
  const [userId, setUserId] = useState<string>("")

  const [newFolderOpen, setNewFolderOpen] = useState(false)
  const [newFolderName, setNewFolderName] = useState("")
  const [renameFolder, setRenameFolder] = useState<AlbumFolder | null>(null)
  const [renameValue, setRenameValue] = useState("")
  const [deleteFolder, setDeleteFolder] = useState<AlbumFolder | null>(null)
  const [deletePicture, setDeletePicture] = useState<Picture | null>(null)
  const [busy, setBusy] = useState(false)

  const [uploading, setUploading] = useState(0)
  const fileInput = useRef<HTMLInputElement>(null)
  const activeIdRef = useRef<number | null>(null)
  const dragDepth = useRef(0)
  const [isDraggingFiles, setIsDraggingFiles] = useState(false)

  const [viewerIndex, setViewerIndex] = useState<number | null>(null)

  // Dossiers et informations du projet
  useEffect(() => {
    let cancelled = false
    setLoadingFolders(true)
    fetch(`/social_networks/${sid}/albums.json`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((res) => {
        if (!res.ok) throw new Error(String(res.status))
        return res.json()
      })
      .then((data: { albums?: AlbumFolder[]; name?: string; user_id?: string | number } | AlbumFolder[]) => {
        if (cancelled) return
        const list = Array.isArray(data) ? data : data.albums ?? []
        if (!Array.isArray(data)) {
          if (data.name) setProjectName(data.name)
          if (data.user_id != null) setUserId(String(data.user_id))
        }
        setFolders(list)
        setActiveId(list[0]?.id ?? null)
      })
      .catch(() => {
        if (cancelled) return
        setDemo(true)
        setFolders(demoFolders)
        setActiveId(demoFolders[0].id)
      })
      .finally(() => !cancelled && setLoadingFolders(false))
    return () => {
      cancelled = true
    }
  }, [sid])

  useEffect(() => {
    activeIdRef.current = activeId
  }, [activeId])

  const loadPictures = useCallback(
    (albumId: number) => {
      setLoadingPictures(true)
      fetch(`/social_networks/${sid}/pictures?album_id=${albumId}`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
        .then((res) => {
          if (!res.ok) throw new Error(String(res.status))
          return res.json()
        })
        .then((data: { pictures?: Picture[] } | Picture[]) => {
          setPictures(Array.isArray(data) ? data : data.pictures ?? [])
        })
        .catch(() => setPictures(demo ? demoPictures(albumId) : []))
        .finally(() => setLoadingPictures(false))
    },
    [sid, demo],
  )

  useEffect(() => {
    if (activeId == null) return
    loadPictures(activeId)
  }, [activeId, loadPictures])

  const activeFolder = useMemo(
    () => folders.find((f) => f.id === activeId) ?? null,
    [folders, activeId],
  )

  // Créer un dossier
  const createFolder = () => {
    const name = newFolderName.trim()
    if (!name) return
    setBusy(true)
    postForm(`/social_networks/${sid}/albums`, {
      "album[name]": name,
      social_network_id: sid,
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status))
        const data = await res.json().catch(() => null)
        const created: AlbumFolder =
          (data && (data.album ?? data)) && (data.album ?? data).id
            ? { id: (data.album ?? data).id, name: (data.album ?? data).name ?? name }
            : { id: Date.now(), name }
        setFolders((p) => [...p, created])
        setActiveId(created.id)
      })
      .catch(() => {
        const created = { id: Date.now(), name }
        setFolders((p) => [...p, created])
        setActiveId(created.id)
      })
      .finally(() => {
        setBusy(false)
        setNewFolderOpen(false)
        setNewFolderName("")
      })
  }

  // Renommer
  const submitRename = () => {
    const folder = renameFolder
    const name = renameValue.trim()
    if (!folder || !name || name === folder.name) {
      setRenameFolder(null)
      return
    }
    setBusy(true)
    postForm(`/social_networks/${sid}/albums/${folder.id}`, {
      _method: "patch",
      "album[name]": name,
      social_network_id: sid,
    })
      .catch(() => undefined)
      .finally(() => {
        setFolders((p) => p.map((f) => (f.id === folder.id ? { ...f, name } : f)))
        setBusy(false)
        setRenameFolder(null)
      })
  }

  // Supprimer un dossier
  const confirmDeleteFolder = () => {
    const folder = deleteFolder
    if (!folder) return
    setBusy(true)
    postForm(`/social_networks/${sid}/albums/delete_album`, {
      album_id: String(folder.id),
      social_network_id: sid,
    })
      .catch(() => undefined)
      .finally(() => {
        setFolders((prev) => {
          const next = prev.filter((f) => f.id !== folder.id)
          if (activeId === folder.id) setActiveId(next[0]?.id ?? null)
          return next
        })
        if (activeId === folder.id) setPictures([])
        setBusy(false)
        setDeleteFolder(null)
      })
  }

  // Upload — envoi une par une, même paramètre tableau d'un seul élément
  const onFiles = async (files: FileList | File[] | null, targetAlbumId = activeId) => {
    if (!files || files.length === 0 || targetAlbumId == null || uploading > 0) return
    const list = Array.from(files).filter((file) => file.type.startsWith("image/"))
    if (list.length === 0) return
    setUploading(list.length)
    let okCount = 0
    let failed = false
    for (let i = 0; i < list.length; i++) {
      const f = list[i]
      const form = new FormData()
      form.append("picture[image][]", f)
      try {
        const res = await fetch(`/social_networks/${sid}/albums/${targetAlbumId}/pictures`, {
          method: "POST",
          headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
          body: form,
          credentials: "same-origin",
        })
        if (!res.ok) throw new Error(String(res.status))
        okCount++
      } catch {
        failed = true
      }
      setUploading(list.length - i - 1)
    }
    if (okCount > 0 && !failed) {
      // Toutes réussies : on recharge depuis le serveur.
      if (activeIdRef.current === targetAlbumId) loadPictures(targetAlbumId)
    } else if (okCount === 0) {
      // Aperçu sans backend : on affiche les photos choisies localement.
      const local: Picture[] = list.map((f, i) => {
        const url = URL.createObjectURL(f)
        return { id: Date.now() + i, image_url: url, src: url, is_like: 0 }
      })
      if (activeIdRef.current === targetAlbumId) setPictures((p) => [...local, ...p])
    } else if (activeIdRef.current === targetAlbumId) {
      // Certaines réussies : on recharge la liste côté serveur.
      loadPictures(targetAlbumId)
    }
    setUploading(0)
    if (fileInput.current) fileInput.current.value = ""
  }

  const handleDragEnter = (event: DragEvent<HTMLElement>) => {
    event.preventDefault()
    if (!event.dataTransfer.types.includes("Files") || activeId == null || uploading > 0) return
    dragDepth.current += 1
    setIsDraggingFiles(true)
  }

  const handleDragLeave = (event: DragEvent<HTMLElement>) => {
    event.preventDefault()
    dragDepth.current = Math.max(0, dragDepth.current - 1)
    if (dragDepth.current === 0) setIsDraggingFiles(false)
  }

  const handleDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault()
    dragDepth.current = 0
    setIsDraggingFiles(false)
    const destinationFolderId = activeId
    if (destinationFolderId == null || uploading > 0) return
    void onFiles(event.dataTransfer.files, destinationFolderId)
  }

  // Like — POST /api/like_picture avec user_id, picture_id, is_like
  const toggleLike = (picture: Picture) => {
    const next = picture.is_like ? 0 : 1
    setPictures((p) => p.map((x) => (x.id === picture.id ? { ...x, is_like: next } : x)))
    postForm(`/api/like_picture`, {
      user_id: userId,
      picture_id: String(picture.id),
      is_like: String(next),
    }).catch(() => undefined)
  }

  // Supprimer une photo
  const confirmDeletePicture = () => {
    const picture = deletePicture
    if (!picture) return
    setPictures((p) => p.filter((x) => x.id !== picture.id))
    setDeletePicture(null)
    postForm(`/social_networks/${sid}/pictures/delete_picture`, {
      picture_id: String(picture.id),
      social_network_id: sid,
    }).catch(() => undefined)
  }

  const viewer = viewerIndex != null ? pictures[viewerIndex] : null

  // Navigation clavier dans la visionneuse
  useEffect(() => {
    if (viewerIndex == null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" && viewerIndex > 0) {
        setViewerIndex(viewerIndex - 1)
      } else if (e.key === "ArrowRight" && viewerIndex < pictures.length - 1) {
        setViewerIndex(viewerIndex + 1)
      } else if (e.key === "Escape") {
        setViewerIndex(null)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [viewerIndex, pictures.length])

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-accent/5">
      <Navigation />

      <main className="container mx-auto px-4 lg:px-8 pt-[120px] pb-16">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" size="sm" asChild className="mb-4 -ml-2 text-muted-foreground">
            <Link to="/social_networks">
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              Retour à mes créations
            </Link>
          </Button>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-display font-bold bg-gradient-to-r from-[#f7bbac] to-[#f9bb72] bg-clip-text text-transparent">
                Les photos{projectName ? ` de « ${projectName} »` : " de l'album"}
              </h1>
              <p className="text-muted-foreground mt-2">
                Organisez vos souvenirs par dossier et ajoutez vos plus belles photos.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setNewFolderOpen(true)}>
                <FolderPlus className="w-4 h-4 mr-2" />
                Nouveau dossier
              </Button>
              <Button
                variant="rainbow"
                disabled={activeId == null || uploading > 0}
                onClick={() => fileInput.current?.click()}
              >
                {uploading > 0 ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                {uploading > 0 ? `Envoi de ${uploading} photo${uploading > 1 ? "s" : ""}…` : "Ajouter des photos"}
              </Button>
              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => onFiles(e.target.files)}
              />
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-[260px,1fr] gap-6">
          {/* Dossiers */}
          <aside>
            {/* Mobile : barre défilante */}
            <div className="lg:hidden -mx-4 px-4 pb-2 overflow-x-auto">
              <div className="flex gap-2 w-max">
                {folders.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setActiveId(f.id)}
                    className={cn(
                      "px-4 py-2 rounded-full text-sm whitespace-nowrap border transition-colors",
                      activeId === f.id
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-card text-foreground border-border/60",
                    )}
                  >
                    {f.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Desktop : liste */}
            <Card className="hidden lg:block p-2 border-border/60">
              {loadingFolders && (
                <p className="text-sm text-muted-foreground p-3 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Chargement…
                </p>
              )}
              {!loadingFolders && folders.length === 0 && (
                <p className="text-sm text-muted-foreground p-3">Aucun dossier.</p>
              )}
              <ul className="space-y-1">
                {folders.map((f) => (
                  <li key={f.id}>
                    <div
                      className={cn(
                        "group flex items-center gap-2 rounded-lg px-3 py-2 cursor-pointer transition-colors",
                        activeId === f.id ? "bg-primary/10 text-foreground" : "hover:bg-muted/60",
                      )}
                      onClick={() => setActiveId(f.id)}
                    >
                      <Folder
                        className={cn(
                          "w-4 h-4 shrink-0",
                          activeId === f.id ? "text-primary" : "text-muted-foreground",
                        )}
                      />
                      <span className="flex-1 text-sm font-medium truncate">{f.name}</span>
                      {f.id !== -1 && f.id !== -2 && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <button className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground transition-opacity">
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                setRenameFolder(f)
                                setRenameValue(f.name)
                              }}
                            >
                              <Pencil className="w-4 h-4 mr-2" />
                              Renommer
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => setDeleteFolder(f)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setNewFolderOpen(true)}
                className="w-full flex items-center gap-2 rounded-lg px-3 py-2 mt-1 text-sm text-muted-foreground hover:text-primary hover:bg-muted/60 transition-colors"
              >
                <FolderPlus className="w-4 h-4" />
                Nouveau dossier
              </button>
            </Card>
          </aside>

          {/* Photos */}
          <section
            className="relative min-h-64"
            onDragEnter={handleDragEnter}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {isDraggingFiles && (
              <div className="absolute inset-0 z-30 flex items-center justify-center rounded-lg border-2 border-dashed border-primary bg-background/90 backdrop-blur-sm pointer-events-none">
                <div className="flex flex-col items-center gap-3 text-center px-6">
                  <Upload className="h-9 w-9 text-primary" />
                  <p className="font-display font-semibold text-foreground">
                    Déposez les photos dans « {activeFolder?.name} »
                  </p>
                </div>
              </div>
            )}
            {activeFolder && (
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display font-semibold text-xl text-foreground">
                  {activeFolder.name}
                </h2>
                {!loadingPictures && (
                  <span className="text-sm text-muted-foreground">
                    {pictures.length} photo{pictures.length > 1 ? "s" : ""}
                  </span>
                )}
              </div>
            )}

            {loadingPictures && (
              <div className="flex flex-col items-center justify-center py-20 gap-3 text-muted-foreground">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <p className="text-sm">Chargement des photos…</p>
              </div>
            )}

            {!loadingPictures && pictures.length === 0 && (
              <Card className="border-border/60 bg-card/80 text-center">
                <div className="py-16 px-6 flex flex-col items-center gap-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                    <ImageIcon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-display font-semibold">Aucune photo ici</h3>
                  <p className="text-muted-foreground max-w-sm">
                    Ajoutez vos photos dans ce dossier pour commencer à composer votre album.
                  </p>
                  <Button
                    variant="rainbow"
                    disabled={activeId == null}
                    onClick={() => fileInput.current?.click()}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Ajouter des photos
                  </Button>
                </div>
              </Card>
            )}

            {!loadingPictures && pictures.length > 0 && (
              <div className="relative">
              {uploading > 0 && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-background/70 backdrop-blur-sm rounded-xl">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">
                    Envoi de {uploading} photo{uploading > 1 ? "s" : ""}…
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
                {pictures.map((picture, i) => (
                  <div
                    key={picture.id}
                    className="group relative aspect-square overflow-hidden rounded-xl border border-border/50 bg-muted/40"
                  >
                    <img
                      src={picture.src || picture.image_url}
                      alt=""
                      loading="lazy"
                      className="h-full w-full object-cover cursor-pointer transition-transform duration-500 group-hover:scale-105"
                      onClick={() => setViewerIndex(i)}
                    />
                    <div className="absolute inset-x-0 bottom-0 flex items-center justify-between p-2 bg-gradient-to-t from-black/50 to-transparent opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => toggleLike(picture)}
                        className="rounded-full bg-background/80 backdrop-blur p-1.5 hover:scale-110 transition-transform"
                        title={picture.is_like ? "Retirer le like" : "J'aime"}
                      >
                        <Heart
                          className={cn(
                            "w-4 h-4",
                            picture.is_like ? "fill-primary text-primary" : "text-foreground",
                          )}
                        />
                      </button>
                      <button
                        onClick={() => setDeletePicture(picture)}
                        className="rounded-full bg-background/80 backdrop-blur p-1.5 text-destructive hover:scale-110 transition-transform"
                        title="Supprimer la photo"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              </div>
            )}
          </section>
        </div>
      </main>

      {/* Nouveau dossier */}
      <Dialog open={newFolderOpen} onOpenChange={setNewFolderOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Nouveau dossier</DialogTitle>
            <DialogDescription>Donnez un nom à votre dossier de photos.</DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            placeholder="Nom du dossier"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && createFolder()}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewFolderOpen(false)}>
              Annuler
            </Button>
            <Button variant="rainbow" onClick={createFolder} disabled={busy || !newFolderName.trim()}>
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Renommer */}
      <Dialog open={!!renameFolder} onOpenChange={(o) => !o && setRenameFolder(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Renommer le dossier</DialogTitle>
          </DialogHeader>
          <Input
            autoFocus
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submitRename()}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameFolder(null)}>
              Annuler
            </Button>
            <Button variant="rainbow" onClick={submitRename} disabled={busy}>
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suppression dossier */}
      <AlertDialog open={!!deleteFolder} onOpenChange={(o) => !o && setDeleteFolder(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer « {deleteFolder?.name} » ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le dossier et son organisation seront supprimés. Cette action est définitive.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteFolder}>Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Suppression photo */}
      <AlertDialog open={!!deletePicture} onOpenChange={(o) => !o && setDeletePicture(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette photo ?</AlertDialogTitle>
            <AlertDialogDescription>
              La photo sera retirée de l'album. Cette action est définitive.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeletePicture}>Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Visionneuse */}
      <Dialog open={viewer != null} onOpenChange={(o) => !o && setViewerIndex(null)}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-5xl p-0 overflow-hidden bg-background border-border/60">
          <DialogHeader className="sr-only">
            <DialogTitle>Photo</DialogTitle>
          </DialogHeader>
          {viewer && (
            <div className="relative flex h-[min(80vh,760px)] w-full items-center justify-center bg-muted/40">
              <img
                src={viewer.image_url || viewer.src}
                alt=""
                className="h-full w-full object-contain"
              />
              {/* Compteur XX/XX */}
              {viewerIndex != null && (
                <span className="absolute top-3 left-1/2 -translate-x-1/2 rounded-full bg-background/80 backdrop-blur px-3 py-1 text-sm font-medium text-foreground">
                  {viewerIndex + 1} / {pictures.length}
                </span>
              )}
              {viewerIndex != null && viewerIndex > 0 && (
                <button
                  onClick={() => setViewerIndex(viewerIndex - 1)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-background/80 backdrop-blur p-2"
                  title="Précédent"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}
              {viewerIndex != null && viewerIndex < pictures.length - 1 && (
                <button
                  onClick={() => setViewerIndex(viewerIndex + 1)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-background/80 backdrop-blur p-2"
                  title="Suivant"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={() => toggleLike(viewer)}
                className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-background/80 backdrop-blur px-4 py-2 flex items-center gap-2 text-sm"
              >
                <Heart
                  className={cn(
                    "w-4 h-4",
                    viewer.is_like ? "fill-primary text-primary" : "text-foreground",
                  )}
                />
                {viewer.is_like ? "Aimée" : "J'aime"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  )
}
